import React, { useEffect, useState } from "react";
import Dashboard from "../../layout/Dashboard/Dashboard";
import { Container, Row, Col, Button, Alert } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";

import "./generatedChart.scss";
import PivotTableWithECharts from "../../components/Chart/PivotTableWithECharts";
import GeoMap from "../../components/Chart/GeoMap";
import AirGeoMap from "../../components/Chart/AirGeoMap";
import FusionGeoMap from "../../components/Chart/FusionGeoMap";
import { useCharts } from "../../hooks/useCharts";
import { usePivot } from "../../hooks/usePivot";
import { useDatasets } from "../../hooks/useDatasets";
import "../../i18n";
import CookieManager from "../../components/Cookies/CookieManager";

const EditChartPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chartId = searchParams.get("chartid");
  const { getSpecificDataset } = useDatasets([]);
  const { getOneChart, updateChart } = useCharts();
  const { getTheTable, updateTable } = usePivot();

  const [chartData, setChartData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState("");
  const [updatedCl, setUpdatedCl] = useState([]);
  const [updatedRw, setUpdatedRw] = useState([]);
  const [updateAggName, setUpdateAggName] = useState("Count");
  const [updatevals, setUpdateVals] = useState([]);
  const [updatedChartType, setUpdatedChartType] = useState("");
  const [chartName, setChartName] = useState("");
  const [category, setCategory] = useState("");
  const [datasetName, setDatasetName] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!chartId) throw new Error("Chart ID is not specified.");
        const chartDetails = await getOneChart(chartId);
        const tableDetails = await getTheTable(chartDetails.pivotId);
        const datasetDetails = await getSpecificDataset(tableDetails.datasetId);
        setChartData(chartDetails);
        setTableData(tableDetails);
        setChartName(chartDetails.name);
        setDataset(datasetDetails);
        setCategory(datasetDetails.category);
        setDatasetName(datasetDetails.datasetName);
        setSelectedChartType(chartDetails.type);
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setError("Failed to load chart data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chartId]);

  const handlePivotStateChange = (
    rendererName,
    cols,
    rows,
    aggregatorName,
    vals
  ) => {
    setUpdatedCl(cols);
    setUpdatedRw(rows);
    setUpdateAggName(aggregatorName);
    setUpdateVals(vals);
    setUpdatedChartType(rendererName);
  };

  const handleUpdateChart = async (e) => {
    e.preventDefault();

    const chartElement = document.querySelector(".svg-container");
    const mapsElement = document.getElementById(chartName) || null;

    if (!chartElement && !mapsElement) {
      console.error("Chart container not found");
      return;
    }
    let cavasElement;
    if (chartElement) {
      cavasElement = chartElement;
    } else if (mapsElement) {
      cavasElement = mapsElement;
    }

    try {
      const canvas = await html2canvas(cavasElement, {
        backgroundColor: null,
        useCORS: true,
      });

      const imageBase64 = canvas.toDataURL("image/png");

      // Update the table with new columns and rows if they are provided
      if (updatedRw.length || updatedCl.length) {
        await updateTable(chartData.pivotId, {
          selectedColumns: updatedCl,
          selectedRows: updatedRw,
        });
      }

      // Update the table to set data to null if it's a geographical chart
      if (chartData.type === "Geographical Chart") {
        await updateTable(chartData.pivotId, { data: null });
      }

      // Add Base64 image to chartData
      const chartDataUpdate = {
        type: updatedChartType,
        width: "100%",
        height: "350",
        renderAt: updateAggName,
        chartConfig: {
          subcaption: chartData.pivotId,
          theme: updatevals[0],
        },
        imageBase64, // Save the Base64 image
      };

      await updateChart(chartId, chartDataUpdate);
      navigate("/single-dashboard");
    } catch (error) {
      console.error("Error capturing chart:", error);
    }
  };

  if (loading) {
    return (
      <Dashboard>
        <Container>Loading...</Container>
      </Dashboard>
    );
  }

  if (error) {
    return (
      <Dashboard>
        <Container>
          <Alert variant="danger">{error}</Alert>
        </Container>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <CookieManager />
      <Container fluid>
        <div className="saveContainer">
          <Container className="button-container">
            <Row>
              <Col>
                <Button
                  className="btn btn-success px-3 py-1 fw-bold mt-5"
                  onClick={handleUpdateChart}
                >
                  Update Chart
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
        <h4 className="createChartHeader">Chart Name: {chartData.name}.</h4>
        <h4 className="createChartHeader">Selected Dataset: {datasetName}.</h4>
        {selectedChartType === "Geographical Chart" &&
        category === "fusion" ? (
          <FusionGeoMap
            data={dataset.elements}
            chartName={chartData.name}
            selectedRiskFactor={chartData.chartConfig?.selectedRiskFactor || null}
            selectedDatasetId={chartData.chartConfig?.selectedDatasetId || tableData?.datasetId || null}
            riskFactors={[]}
          />
        ) : selectedChartType === "Geographical Chart" &&
          category === "Factors Related to Screening" ? (
          <GeoMap
            data={dataset.elements}
            chartName={chartData.name}
            category={chartData.chartConfig?.caption}
            style={{ width: "100%", height: "400px" }}
            label={datasetName}
          />
        ) : selectedChartType === "Geographical Chart" &&
          category === "Air Pollution" ? (
          <AirGeoMap
            data={dataset.elements}
            chartName={chartData.name}
            category={chartData.chartConfig?.caption}
            label={datasetName}
          />
        ) : (
          <PivotTableWithECharts
            data={dataset.elements}
            initialPivotState={{
              data: dataset.elements,
              rows: tableData.selectedRows,
              cols: tableData.selectedColumns,
              aggregatorName: chartData.renderAt,
              vals: [chartData.chartConfig.theme],
              rendererName: chartData.type,
            }}
            onChange={handlePivotStateChange}
          />
        )}
      </Container>
    </Dashboard>
  );
};
export default EditChartPage;
